import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import IconButton from "@mui/material/IconButton";
import InfoIcon from '@mui/icons-material/Info';
import useMediaQuery from "@mui/material/useMediaQuery";

import HelperTextButton from "@refere/ui-utils/ui/components/HelperTextButton"
import ErrorMessage from "@refere/ui-utils/ui/components/ErrorMessage"
import FormModifierWrapper from "@refere/ui-utils/ui/components/FormModifierWrapper"
import TextBox from "@refere/ui-utils/ui/components/TextBox"

import { useHookstate } from "@hookstate/core";
import { object, string } from 'yup';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';

import { mostraFormatadoEmReais } from "@refere/ui-utils/ui/utils/format";

import { globalState } from "../../../../features/global-state";
import { IPassoSeguroState, savePassoSeguro } from "../../../../features/pages/seguro/passo-seguro";

const schema = object({
    nome: string().nullable()
        .required("Nome é obrigatório")
        .max(100, "Nome deve ter no máximo ${max} caracteres"),
    nomeSocial: string().nullable()
        .max(250, "Nome deve ter no máximo ${max} caracteres"),
    codPlano: string().nullable()
        .required("Deve ser escolhido um Plano")
});

interface ICabecalhoNomePlanos{
    planosNomes: string[]
    width: string
}

const CabecalhoNomePlanos = ({planosNomes, width}: ICabecalhoNomePlanos) => 
    <Box width={width} display="flex" flexDirection="row" justifyContent="space-between">
        {
            (planosNomes ?? []).map((p, i) =>
                <Typography key={i} fontSize="14px" width={`calc(${width} / ${planosNomes.length})`} textAlign="center">
                    {p}
                </Typography>
            )
        }
    </Box>

const PassoSeguro = () => {
    const passoSeguroState = useHookstate(globalState.passoSeguro);
    
    const nomeSocial = passoSeguroState?.get({ noproxy: true })?.nomeSocial;
    const [mostrarNomeSocial, setMostrarNomeSocial] = useState(!!nomeSocial);
    
    const { control, setValue, watch, trigger, formState: { errors }, handleSubmit, getFieldState } = useForm<IPassoSeguroState>({
        mode: "onBlur",
        resolver: yupResolver(schema as any)
    });

    useEffect(() => {
        const passoSeguro = passoSeguroState.get({ noproxy: true });
        if (passoSeguro) {
            for(const field in passoSeguro) {
                setValue(field as any, passoSeguro[field]);
            }
        }
    }, [ passoSeguroState ]);

    const match650 = useMediaQuery("(max-width: 650px)");

    const planosNomes = passoSeguroState?.get({noproxy: true})?.planos.map(item => item.nome)

    return (
        <form onSubmit={
            async (e) => {
                e.preventDefault();
                await handleSubmit(async (validvalue: IPassoSeguroState) => {
                    await savePassoSeguro(validvalue);
                })();
            }
        }>
            <Stack direction="column" mt={2}>
                <FormModifierWrapper 
                    name="nome"
                    control={control}
                    hideErrorMessage={!getFieldState('nome').invalid}
                    render={({ field, fieldState }) => 
                        <TextBox 
                            { ...field }
                            label="Nome Completo"
                            value={field.value}
                            error={fieldState.invalid}
                        />
                    }
                />
                <Box 
                    width = "100%" 
                    marginTop = "5px"
                    marginBottom = { mostrarNomeSocial ? "5px": "20px" }
                    onClick = {() => {
                        setMostrarNomeSocial(prev => {
                            const newValue = !prev;

                            // se ocultar o campo → salvar NULL no form
                            if (!newValue) {
                                setValue("nomeSocial", null);
                            }

                            return newValue;
                        });
                    }}
                    sx = {{ cursor: "pointer", display: "flex", alignItems: "center"}}
                >
                    <Typography fontSize={10}>
                        <IconButton size="small" sx={{ padding: 0 }}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                        Informar Nome Social
                    </Typography>
                </Box>
                {
                    mostrarNomeSocial &&
                        <FormModifierWrapper 
                            name="nomeSocial"
                            control={control}
                            render={({ field, fieldState }) => 
                                <TextBox 
                                    { ...field }
                                    label="Nome Social"
                                    value={field.value ?? ""}
                                    error={fieldState.invalid}
                                    helperText="Nome social é a designação que o indivíduo escolheu 
                                    para lhe representar diante dos demais, por entender que o nome constante em seus registros oficiais 
                                    não condiz com sua personalidade e identidade de gênero."
                                />
                            }
                        />
                }
                <Box maxWidth="170px" mt={1}>
                    <FormModifierWrapper 
                        name="cpf"
                        control={control}
                        render={({ field }) => 
                            <TextBox 
                                { ...field }
                                label="CPF"
                                value={field.value}
                                disabled
                                mask="999.999.999-99"
                            />
                        }
                    />
                </Box>
                <Box width="100%" mt={2} mb={4}>
                    <Typography variant="body2">
                        A vigência do seguro é de 24 meses e iniciará as 24h do dia do pagamento da primeira parcela.
                    </Typography>
                </Box>
                <Box maxWidth="600px" width="100%" display="block" alignSelf="center">
                    <Box alignSelf="center" display="flex" flexDirection="column" >
                        {
                            !match650 &&
                            <Box width="100%" display="flex" flexDirection="row" justifyContent="flex-end">
                                <CabecalhoNomePlanos planosNomes={planosNomes} width="50%"/>
                            </Box>
                        }
                        <Box>
                        {
                            (passoSeguroState?.get({ noproxy: true })?.coberturas ?? []).map((c, ic) => 
                                <Box display="flex" flexDirection={match650 ? "column" : "row"} key={ic} >
                                    <Box width={match650 ? "100%" : "50%"}>
                                        <Typography variant="caption" fontSize="14px">
                                            {c.nome}
                                            <HelperTextButton helperText={c.helperText}/>
                                        </Typography>
                                    </Box>
                                    {
                                        match650 &&
                                        <CabecalhoNomePlanos planosNomes={planosNomes} width="100%"/>
                                    }
                                    <Box width={match650 ? "100%" : "50%"} display="flex" flexDirection="row" justifyContent="space-between">
                                    {
                                        c.valores.map((v, iv) => 
                                            <Box width={match650 ? `calc(100% / ${c.valores.length})` : `calc(50% / ${c.valores.length})`} 
                                                textAlign="center" key={iv}>
                                                <Typography  variant="caption">
                                                    {mostraFormatadoEmReais(v.valor)}
                                                </Typography>
                                            </Box>
                                        )
                                    }
                                    </Box>
                                </Box>
                            )
                        }
                        </Box>
                        <Box display="flex" flexDirection={match650 ? "column" : "row"} mt={1}>
                            <Box width={match650 ? "100%" : "50%"} textAlign={match650 ? "left" : "right"} pr={2}>
                                <Typography fontSize="14px">
                                    Prêmio Total
                                </Typography>
                            </Box> 
                            {
                                match650 &&
                                <CabecalhoNomePlanos planosNomes={planosNomes} width="100%"/>
                            }
                            <Box width={match650 ? "100%" : "50%"} display="flex" flexDirection="row" justifyContent="space-between">
                            {
                                (passoSeguroState?.get({noproxy: true})?.planos ?? []).map(p =>{
                                    const planos = passoSeguroState.get({noproxy: true}).planos
                                    return(
                                        <Box width={match650 ? `calc(100% / ${planos.length})` : `calc(50% / ${planos.length})`} textAlign="center" key={p.codPlano}>
                                            <Typography variant="caption">
                                                {mostraFormatadoEmReais(p.vlrPremio)}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection={match650 ? "column" : "row"}  alignItems="center">
                            <Box width={match650 ? "100%" : "50%"} textAlign={match650 ? "left" : "right"} pr={2}>
                                <Typography fontSize="14px">
                                    Contratar
                                </Typography>
                            </Box>
                            {
                                match650 &&
                                <CabecalhoNomePlanos planosNomes={planosNomes} width="100%"/>
                            }
                            <Box width={match650 ? "100%" : "50%"} display="flex" flexDirection="row" justifyContent="space-between">
                            {
                                (passoSeguroState?.get({noproxy: true})?.planos ?? []).map((p, ip) =>{
                                    const planos = passoSeguroState?.get({noproxy: true})?.planos;
                                    return (
                                        <Box width={match650 ? `calc(100% / ${planos.length})` : `calc(50% / ${planos.length})`}textAlign="center" key={ip}>
                                            <Radio 
                                                sx={{
                                                    "svg": {
                                                        width: "1.5em",
                                                        height: "1.5em"
                                                    }
                                                }}
                                                checked={watch("codPlano") === p.codPlano} 
                                                size="medium" 
                                                color="primary"
                                                onChange={async (e) => {
                                                    if (e.target.checked)
                                                        setValue("codPlano", p.codPlano);
                                                    
                                                    await trigger("codPlano");
                                                }}
                                            />
                                        </Box>
                                    )
                                })
                            }
                            </Box>
                        </Box>
                        {
                            errors?.codPlano?.message &&
                            <Box display="flex" width={match650 ? "100%" : "50%"} alignSelf="flex-end" justifyContent="center" >
                                <ErrorMessage errorMessage={errors.codPlano.message} />
                            </Box>
                        }
                    </Box>
                </Box>
            </Stack>
            <Box sx={{ float: "right" }}>
                <Button 
                    sx={{
                        mt: 2,
                        textTransform: "none",
                        minWidth: "200px"
                    }}
                    variant="contained"
                    endIcon={<ArrowCircleRightIcon />}
                    type="submit" >
                    Próximo
                </Button>
            </Box>
        </form>
    );

}

export default PassoSeguro;